import { PoweredBySkor } from "../../components/design";
import { getEffortRateType } from "../../helper/profileCalculations";
/* eslint-disable no-console */
// ──────────────────────────────────────────────────────────────
// LinkedReportOverview — landlord report with SDK + snapshot support
// ──────────────────────────────────────────────────────────────

// ── React & routing ───────────────────────────────────────────
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

// ── Third-party libraries ─────────────────────────────────────
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import "moment/locale/en-gb";
import "moment/locale/es";
import { Modal, TextArea } from "../../components/design";

// ── Assets & styles ───────────────────────────────────────────
import {
  Avatar,
  Badge,
  Button,
  Icon,
  PageHeader,
  Notice,
  Skeleton,
  getStatusBadgeTone,
} from "../../components/design";
import "../../styles/linked-report-overview.css";

// ── Components ────────────────────────────────────────────────
import Loader from "../../components/Loader";
import DecisionCard from "../../components/Property/DecisionCard";
import PolicyMetricSection from "../../components/Property/PolicyMetricSection";
import ProfileUpdateRequestModal from "../../components/TenantDetails/ProfileUpdateRequestModal";
import RequestTenantUpdateModal from "../../components/RequestTenantUpdateModal";
import VerdictPanel from "../../components/Property/VerdictPanel";
import {
  buildHardFailWarnings,
  buildBlockingRows,
  isGuarantorOutsideSpainHardFail,
} from "../../helper/verdictHelpers";

// ── Contexts ──────────────────────────────────────────────────
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";

// ── Helpers & utils ───────────────────────────────────────────
import {
  calculateFinalScore,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getIdentityDetails,
  getKycResolutionStatus,
  getRentPercentage,
  haveValue,
  resolveIncomeSelection,
  toast,
} from "../../helper/common";
import {
  getPillarBadgeFromScore,
  PILLAR_NO_DATA_KEY,
  PILLAR_TITLE_KEYS,
} from "../../helper/pillarStatus";
import { computeFinalResult } from "../../Utils/computeFinalResult";
import { getMissingDocsFromSnapshot } from "../../Utils/getMissingDocsFromSnapshot";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";

// ── Services ──────────────────────────────────────────────────
import axiosInstance from "../../services/authWrapper";
import {
  createProfileReminder,
  getProfileReminders,
} from "../../services/sharedService";

// ── Hooks & constants ─────────────────────────────────────────
import useTranslator from "../../hooks/useTranslator";
import { isSkorSdkMode, isSdkLangExplicit } from "../../helper/skorSdk";
import {
  PILLAR_SUB_SCORES,
  SUB_SCORE_TRANSLATIONS,
} from "../../constants/policyConstants";
import {
  isReportPersonProcessing,
  getReportDecisionPeople,
  getProcessingReportApplicants,
} from "../../helper/reportProcessing";
import { getSavedIncomeComponents } from "../../helper/payslipSelection";

// ──────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  yellow: {
    backgroundColor: "var(--warning-soft)",
    color: "var(--warning-text)",
    border: "var(--border-1) solid var(--warning-line)",
  },
  green: {
    backgroundColor: "var(--lime-soft)",
    color: "var(--ink)",
    border: "var(--border-1) solid var(--lime)",
  },
  red: {
    backgroundColor: "var(--danger-soft)",
    color: "var(--danger-text)",
    border: "var(--border-1) solid var(--danger)",
  },
};

// ──────────────────────────────────────────────────────────────
// Pure helpers (no hooks)
// ──────────────────────────────────────────────────────────────

const formatDateOfBirth = (dob, currentLang) => {
  if (!dob) return currentLang === "es" ? "Sin datos" : "No data";
  const formattedDate = moment(dob).format("DD/MM/YYYY");
  if (!moment(formattedDate, "DD/MM/YYYY").isValid()) {
    return currentLang === "es" ? "Sin datos" : "No data";
  }
  return formattedDate;
};

const formatMonthYear = (date, lang) => {
  if (!date) return "";
  const m = moment(date);
  if (!m.isValid()) return "";
  return m.locale(lang === "es" ? "es" : "en-gb").format("MMM YYYY");
};

const normalizeMissingDocuments = (values, language) => {
  const documents = Array.isArray(values) ? values : values ? [values] : [];
  return documents
    .map((value) =>
      typeof value === "string"
        ? value
        : value?.localDocumentName?.[language] ||
          value?.documentNameTranslated?.[language] ||
          value?.localDocumentName?.en ||
          value?.documentNameTranslated?.en ||
          value?.documentName ||
          value?.name ||
          value?.label ||
          ""
    )
    .filter(Boolean);
};

const buildDisplayLabel = (person) => {
  if (haveValue(person?.forensic_name)) return person.forensic_name;
  if (haveValue(person?.onfido_user_kyc?.kyc_data?.first_name)) {
    return `${person.onfido_user_kyc.kyc_data.first_name} ${person.onfido_user_kyc.kyc_data.last_name ?? ""}`.trim();
  }
  return `${person?.fname ?? "N/A"} ${person?.lname ?? ""}`.trim();
};

const getGuarantorWeight = (score) => {
  if (score < 65) return 0.6;
  if (score <= 80) return 0.75;
  return 1;
};

const getInsurableStatusFromCache = (cached) => {
  if (typeof cached?.reportInsurableStatus === "string")
    return cached.reportInsurableStatus;
  if (cached?.reportInsurableStatus === true) return "insurable";
  if (cached?.reportInsurableStatus === false) return "under_review";
  return null;
};

const getDecisionLabel = (apiReport, fallbackStatus) => {
  if (apiReport?.decision) return apiReport.decision.en || apiReport.decision;
  if (fallbackStatus === "approved") return "Approved";
  if (fallbackStatus === "declined") return "Rejected";
  return "Conditional";
};

const resolveWorkspaceId = (
  workspaceIdParam,
  effectiveWorkspaceUuidState,
  user
) => {
  if (
    effectiveWorkspaceUuidState &&
    effectiveWorkspaceUuidState !== "undefined"
  ) {
    return effectiveWorkspaceUuidState;
  }
  if (workspaceIdParam && workspaceIdParam !== "undefined")
    return workspaceIdParam;

  const pathMatch = window.location.pathname.match(
    /^\/([A-Za-z0-9]{6})(?:\/|$)/
  );
  if (pathMatch && pathMatch[1] !== "undefined") return pathMatch[1];
  if (user?.current_workspace?.uuid) return user.current_workspace.uuid;

  const stored =
    localStorage.getItem("current_workspace_uuid") ||
    localStorage.getItem("current_workspace");
  if (stored && /^[A-Za-z0-9]{6}$/.test(stored)) return stored;

  return workspaceIdParam;
};

// Resolve missing statuses before using any income, score or cached decision.
// Older API deployments omit forensic_status from the extra-data response.
const hydrateReportStatuses = async (tenants) => {
  const people = new Map();
  tenants.forEach((tenant) => {
    [tenant, ...(tenant.gurantor_id || [])].forEach((person) => {
      const id = person._id ?? person.id ?? person.tenant_id;
      if (id) {
        const records = people.get(String(id)) || [];
        records.push(person);
        people.set(String(id), records);
      }
    });
  });
  await Promise.all(
    [...people.entries()].map(async ([id, records]) => {
      const knownStatus = records.find((person) =>
        haveValue(person.forensic_status)
      )?.forensic_status;
      if (haveValue(knownStatus)) {
        records.forEach((person) => {
          person.forensic_status = knownStatus;
        });
        return;
      }
      let status = null;
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/tenants/checkForensicAnalysisStatus?tenant_id=${encodeURIComponent(id)}&fresh=false`
        );
        status = response?.data?.success_res?.forensic_status ?? null;
      } catch {
        // Unverified profiles stay processing; partial data must not decide a case.
        status = null;
      }
      records.forEach((person) => {
        person.forensic_status = status;
      });
    })
  );
  return tenants;
};

// ──────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────

const LinkedReportOverview = () => {
  const { _t, i18n } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId: workspaceIdParam } = useParams();

  const {
    getPropertyDetails,
    getTenantExtraData,
    getRiskAssessmentReport,
    deaWorkspace,
    customPolicyDetails,
    getPolicyDetails,
    markProfileResolved,
    getProfileResolutionStatus,
    unmarkProfileResolved,
    sendNoteToTenant,
  } = useContext(LandLordContext);
  const { user, loadUser } = useContext(AuthContext);
  console.log("Landlord user", user);

  // ── Self-hydrate user when rendered outside Homepage (e.g. /linked-report-overview standalone / SDK) ──
  useEffect(() => {
    if (!user) {
      const token = localStorage.getItem("token");
      const isVerified = localStorage.getItem("is_verified");
      const storedUserType = localStorage.getItem("usertype");
      if (token && isVerified === "true") {
        // LinkedReportOverview is landlord-only; fall back to landlord if usertype missing
        loadUser(storedUserType || "landlord");
      }
    }
  }, [user]); // eslint-disable-line

  // ── Query parsing ───────────────────────────────────────────
  const queryParameters = new URLSearchParams(window.location.search);

  const propertyIdParam =
    queryParameters.get("propertyId") ||
    queryParameters.get("property_id") ||
    queryParameters.get("property");
  const applicationIdParam =
    queryParameters.get("applicationId") ||
    queryParameters.get("application_id") ||
    queryParameters.get("application");
  const sharedProfileIds = queryParameters.getAll("sharedprofileid");
  const reportStateKey = queryParameters.get("reportStateKey");
  const rent = Number(queryParameters.get("rent") || 0);
  const selectionRaw = queryParameters.get("selection");
  const awardSnapshotCompleted =
    queryParameters.get("awardSnapshotCompleted") === "true";
  const historyEventId = queryParameters.get("historyEventId");
  const requestedAggregationStrategy = ["MIN", "MAX", "MEAN"].includes(
    queryParameters.get("aggregationStrategy")?.toUpperCase()
  )
    ? queryParameters.get("aggregationStrategy").toUpperCase()
    : null;

  const isSkorSdk = isSkorSdkMode();

  // ── Selection parsing ───────────────────────────────────────
  const selectionModel = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return Array.isArray(model) ? model : null;
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const selectionSelectedIds = useMemo(() => {
    if (!selectionModel) return null;
    return selectionModel.flatMap((entry) => {
      if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
      return (entry.participants || []).map((p) => p.tenantId).filter(Boolean);
    });
  }, [selectionModel]);

  const selectedProfileIdMap = useMemo(() => {
    if (!selectionModel) return {};
    return selectionModel.reduce((map, entry) => {
      const sharedProfileId = entry?.sharedProfileId;
      if (!sharedProfileId) return map;

      if (entry.type === "tenant") {
        if (entry.tenantId) map[entry.tenantId] = sharedProfileId;
        (entry.guarantorIds || []).forEach((guarantorId) => {
          if (guarantorId) map[guarantorId] = sharedProfileId;
        });
        return map;
      }

      (entry.participants || []).forEach((participant) => {
        if (participant?.tenantId) map[participant.tenantId] = sharedProfileId;
        (participant?.guarantorIds || []).forEach((guarantorId) => {
          if (guarantorId) map[guarantorId] = sharedProfileId;
        });
      });
      return map;
    }, {});
  }, [selectionModel]);

  // ── Guarantor ids from URL ────────────────────────────────────
  // New links carry repeated `guarantorid=` params: nested guarantors plus
  // application participants flagged as guarantors (participant.is_guarantor),
  // which selectionModel stores as tenant participants. Merged with the
  // nested guarantorIds already present in selection JSON so old links
  // (without the new param) keep working unchanged.
  const urlGuarantorIds = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = [
        ...params.getAll("guarantorid"),
        ...params.getAll("guarantorId"),
        ...params.getAll("guarantorids"),
        ...params.getAll("guarantorIds"),
      ];
      const split = raw
        .flatMap((v) => String(v).split(","))
        .map((s) => s.trim())
        .filter(Boolean);
      const fromSelection = selectionModel
        ? selectionModel.flatMap((entry) => {
            if (entry.type === "tenant") return entry.guarantorIds || [];
            return (entry.participants || []).flatMap(
              (p) => p.guarantorIds || []
            );
          })
        : [];
      return [
        ...new Set(
          [...split, ...fromSelection].map((id) => String(id)).filter(Boolean)
        ),
      ];
    } catch {
      return [];
    }
  }, [selectionRaw, selectionModel]);

  const cachedReportState = useMemo(() => {
    if (!reportStateKey) return null;
    try {
      const cachedValue = sessionStorage.getItem(reportStateKey);
      if (!cachedValue) return null;
      const parsedValue = JSON.parse(cachedValue);
      if (!parsedValue?.expiresAt || parsedValue.expiresAt <= Date.now()) {
        sessionStorage.removeItem(reportStateKey);
        return null;
      }
      return parsedValue;
    } catch (error) {
      console.log("[LinkedReportOverview] cache read error", error);
      return null;
    }
  }, [reportStateKey]);

  // ── State ───────────────────────────────────────────────────
  const [addressVal, setAddressVal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState("loading");
  const [profiles, setProfiles] = useState([]);
  const [decisionReasons, setDecisionReasons] = useState([]);
  const [status, setStatus] = useState("declined");
  const [combinedIncome, setCombinedIncome] = useState(0);
  const [combinedEffortRate, setCombinedEffortRate] = useState("No data");
  const [lastVerdict, setLastVerdict] = useState(null);
  const [lastRecommendationFlags, setLastRecommendationFlags] = useState({});
  const [localReport, setLocalReport] = useState(null);
  const [apiReport, setApiReport] = useState(null);
  const [processingApplicants, setProcessingApplicants] = useState([]);
  const [processingRefreshKey, setProcessingRefreshKey] = useState(0);
  const [cachedInsurableStatus, setCachedInsurableStatus] = useState(null);
  const [cachedInsuranceErrorCode, setCachedInsuranceErrorCode] =
    useState(null);
  const [cachedInsuranceErrorCodes, setCachedInsuranceErrorCodes] = useState(
    []
  );
  const [showSpinner, setShowSpinner] = useState(false);
  const [showOutdatedModal, setShowOutdatedModal] = useState(false);
  const [showRequestUpdateModal, setShowRequestUpdateModal] = useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderSentAt, setReminderSentAt] = useState(null);
  const [remindersByTenant, setRemindersByTenant] = useState({});
  const [selectedResolveTenantIds, setSelectedResolveTenantIds] = useState([]);
  const [resolvingDocs, setResolvingDocs] = useState(false);
  const [showResolveNoteModal, setShowResolveNoteModal] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [individualResolutions, setIndividualResolutions] = useState({});
  const [individualWarnings, setIndividualWarnings] = useState({});
  const [kycWarnings, setKycWarnings] = useState({});
  const [rawTenantsById, setRawTenantsById] = useState({});
  const [isDocsWarningsLoading, setIsDocsWarningsLoading] = useState(false);
  const [isKycWarningsLoading, setIsKycWarningsLoading] = useState(false);
  const isVerdictLoading = isDocsWarningsLoading || isKycWarningsLoading;
  const [idToSharedProfileId, setIdToSharedProfileId] =
    useState(selectedProfileIdMap);
  const [aggregationStrategy, setAggregationStrategy] = useState(
    requestedAggregationStrategy ||
      cachedReportState?.aggregationStrategy ||
      customPolicyDetails?.aggregationStrategy ||
      "MIN"
  );
  const aggregationStrategyRef = useRef(aggregationStrategy);
  const [calculationInputs, setCalculationInputs] = useState(null);

  // Effective values resolved from backend / property fallback
  const [effectiveRentState, setEffectiveRentState] = useState(rent);
  const [effectivePropertyIdState, setEffectivePropertyIdState] =
    useState(propertyIdParam);
  const [effectiveSharedProfileIdsState, setEffectiveSharedProfileIdsState] =
    useState(sharedProfileIds);
  const [effectiveWorkspaceUuidState, setEffectiveWorkspaceUuidState] =
    useState(null);
  const [giveInsurance] = useState(false);

  useEffect(() => {
    if (
      requestedAggregationStrategy ||
      cachedReportState?.aggregationStrategy
    ) {
      return;
    }
    const nextStrategy = customPolicyDetails?.aggregationStrategy || "MIN";
    aggregationStrategyRef.current = nextStrategy;
    setAggregationStrategy(nextStrategy);
  }, [
    customPolicyDetails?.aggregationStrategy,
    requestedAggregationStrategy,
    cachedReportState?.aggregationStrategy,
  ]);

  // ── Derived values ──────────────────────────────────────────
  const workspaceId = useMemo(
    () =>
      resolveWorkspaceId(workspaceIdParam, effectiveWorkspaceUuidState, user),
    [
      workspaceIdParam,
      user?.current_workspace?.uuid,
      effectiveWorkspaceUuidState,
    ]
  );

  const currentLang = i18n.language?.toLowerCase().startsWith("en")
    ? "en"
    : "es";

  const todaysDate = useMemo(
    () =>
      (createdAt = null, locale = localStorage.getItem("i18nextLng")) => {
        moment.locale(locale);
        return moment(createdAt || undefined).format("D MMM YYYY");
      },
    []
  );

  const getIncomeForRecord = useMemo(
    () =>
      (
        record,
        incomeSelection,
        customIncomeSources,
        savedPayslipIds = null
      ) => {
        const income = getCustomIncomeDetails(
          record,
          user,
          _t,
          incomeSelection,
          undefined,
          undefined,
          deaWorkspace,
          customIncomeSources,
          savedPayslipIds
        );
        return Number(income?.amount || 0);
      },
    [_t, user, deaWorkspace]
  );

  // ── Effects ─────────────────────────────────────────────────

  // Keep shared-profile map in sync with URL selection
  useEffect(() => {
    if (!Object.keys(selectedProfileIdMap).length) return;
    setIdToSharedProfileId((currentMap) => ({
      ...currentMap,
      ...selectedProfileIdMap,
    }));
  }, [selectedProfileIdMap]);

  // Auto-expire cached report state
  useEffect(() => {
    if (!cachedReportState || !reportStateKey) return;
    const removeCache = () => sessionStorage.removeItem(reportStateKey);
    const remainingTime = Math.max(cachedReportState.expiresAt - Date.now(), 0);
    const timeoutId = window.setTimeout(removeCache, remainingTime);
    return () => window.clearTimeout(timeoutId);
  }, [cachedReportState, reportStateKey]);

  // Main data loading (cached vs live)
  useEffect(() => {
    if (!Object.keys(customPolicyDetails?.scoringConfig || {}).length) return;
    if (!user) return;

    const loadData = async () => {
      setLoading(true);
      setReportLoading("loading");
      setProcessingApplicants([]);
      setCachedInsurableStatus(null);
      setCachedInsuranceErrorCode(null);
      setCachedInsuranceErrorCodes([]);

      try {
        // Fast path: hydrate from sessionStorage cache
        if (cachedReportState && processingRefreshKey === 0) {
          const cachedPeopleResponse = effectiveSharedProfileIdsState.length
            ? await getTenantExtraData(effectiveSharedProfileIdsState).catch(
                () => null
              )
            : null;
          const cachedPeople = await hydrateReportStatuses(
            cachedPeopleResponse?.success_res || []
          );
          const cachedPersonMap = {};
          cachedPeople.forEach((tenant) => {
            [tenant, ...(tenant.gurantor_id || [])].forEach((person) => {
              const id = person._id ?? person.id ?? person.tenant_id;
              if (id) cachedPersonMap[String(id)] = person;
            });
          });
          const cachedApplicants =
            cachedReportState.localReport?.report?.applicants || [];
          const canReuseCache =
            cachedApplicants.length > 0 &&
            cachedApplicants.every(
              (applicant) =>
                cachedPersonMap[String(applicant._id)] &&
                !isReportPersonProcessing(
                  cachedPersonMap[String(applicant._id)]
                )
            ) &&
            Object.values(cachedPersonMap).every(
              (person) => !isReportPersonProcessing(person)
            );
          if (canReuseCache) {
            setRawTenantsById(cachedPersonMap);
            const cachedLocalReport = cachedReportState.localReport ?? null;
            const cachedApiReport = cachedReportState.apiReport ?? null;

            setAddressVal(cachedReportState.addressVal ?? null);
            setCombinedIncome(Number(cachedReportState.combinedIncome || 0));
            setCombinedEffortRate(
              cachedReportState.combinedEffortRate ?? "No data"
            );
            setCachedInsurableStatus(
              getInsurableStatusFromCache(cachedReportState)
            );
            setCachedInsuranceErrorCode(
              cachedReportState.reportInsuranceErrorCode ?? null
            );
            setCachedInsuranceErrorCodes(
              cachedReportState.reportInsuranceErrorCodes ?? []
            );
            setLocalReport(cachedLocalReport);
            setDecisionReasons(cachedLocalReport?.report?.recommendation ?? []);
            setStatus(
              cachedLocalReport?.report?.case?.decision === "Approved"
                ? "approved"
                : cachedLocalReport?.report?.case?.decision === "Rejected"
                  ? "declined"
                  : "review"
            );
            setLoading(false);

            if (cachedApiReport) {
              setApiReport(cachedApiReport);
              setReportLoading("loaded");
              return;
            }

            if (cachedLocalReport) {
              setReportLoading("loading");
              getRiskAssessmentReport(cachedLocalReport)
                .then((response) => {
                  if (response?.data?.report) {
                    setApiReport(response.data.report);
                  } else {
                    setApiReport(cachedLocalReport.report);
                  }
                  setReportLoading("loaded");
                })
                .catch((error) => {
                  console.log("[LinkedReportOverview] cached API error", error);
                  setApiReport(cachedLocalReport.report);
                  setReportLoading("loaded");
                });
              return;
            }

            setReportLoading("failed");
            return;
          }
        }

        // ── Resolve canonical ids from applicationId (SDK /redirect flow) ──
        let effectivePropertyId = propertyIdParam;
        let effectiveSharedProfileIds = [...sharedProfileIds];
        let effectiveRent = rent;
        let backendSelectionIds = null;
        let backendIdMap = null;
        let backendWorkspaceUuid = null;

        if (applicationIdParam && effectiveSharedProfileIds.length === 0) {
          try {
            const resolveRes = await axiosInstance.get(
              `${process.env.REACT_APP_SERVER_URL}/shared-profile/by-application/${applicationIdParam}${effectivePropertyId ? `?propertyId=${effectivePropertyId}` : ""}`,
              {
                headers: {
                  "Accept-Language": localStorage.getItem("i18nextLng"),
                },
              }
            );
            const resolved = resolveRes.data?.data;
            if (resolved?.propertyId) effectivePropertyId = resolved.propertyId;
            if (
              Array.isArray(resolved?.sharedProfileIds) &&
              resolved.sharedProfileIds.length
            ) {
              effectiveSharedProfileIds = resolved.sharedProfileIds;
            }
            if (!effectiveRent && resolved?.rent)
              effectiveRent = Number(resolved.rent) || effectiveRent;
            if (!effectiveRent && resolved?.property?.report_rent)
              effectiveRent =
                Number(resolved.property.report_rent) || effectiveRent;
            if (!effectiveRent && resolved?.property?.price)
              effectiveRent = Number(resolved.property.price) || effectiveRent;
            if (!effectiveRent && resolved?.property?.rent)
              effectiveRent = Number(resolved.property.rent) || effectiveRent;

            if (resolved?.workspaceUuid)
              backendWorkspaceUuid = resolved.workspaceUuid;
            else if (
              resolved?.workspaceId &&
              /^[A-Za-z0-9]{6}$/.test(resolved.workspaceId)
            ) {
              backendWorkspaceUuid = resolved.workspaceId;
            }

            if (
              !selectionSelectedIds &&
              Array.isArray(resolved?.selection) &&
              resolved.selection.length
            ) {
              try {
                const ids = resolved.selection.flatMap((e) =>
                  [e.tenantId, ...(e.guarantorIds || [])].filter(Boolean)
                );
                if (ids.length > 0) {
                  backendSelectionIds = ids;
                  backendIdMap = {};
                  resolved.selection.forEach((e) => {
                    const spId = e.sharedProfileId;
                    if (!spId) return;
                    if (e.tenantId) backendIdMap[e.tenantId] = spId;
                    (e.guarantorIds || []).forEach((gid) => {
                      if (gid) backendIdMap[gid] = spId;
                    });
                  });
                }
              } catch {
                // ignore malformed backend selection
              }
            }
          } catch (resolveErr) {
            console.error(
              "[LinkedReportOverview] resolve by applicationId failed",
              resolveErr
            );
          }
        }

        setEffectivePropertyIdState(effectivePropertyId);
        setEffectiveSharedProfileIdsState(effectiveSharedProfileIds);
        setEffectiveRentState(effectiveRent);
        if (backendIdMap && Object.keys(backendIdMap).length) {
          setIdToSharedProfileId((prev) => ({ ...prev, ...backendIdMap }));
        }
        if (backendWorkspaceUuid)
          setEffectiveWorkspaceUuidState(backendWorkspaceUuid);

        // ── Property details (for address + rent + workspace fallback) ──
        let propertyData = null;
        if (haveValue(effectivePropertyId)) {
          const propertyRes = await getPropertyDetails(effectivePropertyId);
          propertyData = propertyRes?.properties ?? null;
          setAddressVal(propertyData);

          if (!effectiveRent && propertyData?.price)
            effectiveRent = Number(propertyData.price) || effectiveRent;
          if (!effectiveRent && propertyData?.rent)
            effectiveRent = Number(propertyData.rent) || effectiveRent;
          if (effectiveRent !== rent) setEffectiveRentState(effectiveRent);

          if (!backendWorkspaceUuid && propertyData?.workspaceIds?.[0]) {
            const firstWsId =
              propertyData.workspaceIds[0]?.toString() ||
              propertyData.workspaceIds[0];
            const wsFromUser =
              user?.workspaceIds?.find(
                (w) =>
                  w?._id?.toString() === firstWsId ||
                  w?.uuid === firstWsId ||
                  w?.toString() === firstWsId
              ) || null;

            if (wsFromUser?.uuid) {
              backendWorkspaceUuid = wsFromUser.uuid;
              setEffectiveWorkspaceUuidState(wsFromUser.uuid);
            } else if (
              user?.current_workspace?.uuid &&
              propertyData.workspaceIds?.some(
                (id) => id.toString() === user.current_workspace._id?.toString()
              )
            ) {
              setEffectiveWorkspaceUuidState(user.current_workspace.uuid);
              backendWorkspaceUuid = user.current_workspace.uuid;
            }
          }
        }

        // ── Fetch tenants (snapshot or live) ──
        let rawTenants = [];

        if (awardSnapshotCompleted || historyEventId) {
          try {
            const snapshotRes = historyEventId
              ? await axiosInstance.get(
                  `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${historyEventId}`
                )
              : await axiosInstance.post(
                  `${process.env.REACT_APP_SERVER_URL}/users/award-snapshot`,
                  {
                    propertyId: effectivePropertyId,
                  }
                );

            const snapshotData = snapshotRes.data?.data;
            const snapshotProfiles = transformAwardSnapshot(snapshotData);
            const snapshotCreatedAt =
              propertyData?.award_snapshot_info?.created_at;

            snapshotProfiles.forEach((profile) => {
              const applySnapshotDocs = (tenantData) => {
                if (!tenantData) return;
                tenantData._snapshotMissingDocs = getMissingDocsFromSnapshot(
                  tenantData,
                  deaWorkspace,
                  snapshotCreatedAt
                );
                (tenantData.gurantor_id || []).forEach((g) => {
                  if (g && typeof g === "object") {
                    g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                      g,
                      deaWorkspace,
                      snapshotCreatedAt
                    );
                  }
                });
              };
              applySnapshotDocs(profile.tenantid);
              (profile.application_id?.participants || []).forEach((p) =>
                applySnapshotDocs(p.tenant_info)
              );
            });

            snapshotProfiles.forEach((profile) => {
              if (profile.tenantid) rawTenants.push(profile.tenantid);
              (profile.application_id?.participants || []).forEach((p) => {
                if (p.tenant_info) rawTenants.push(p.tenant_info);
              });
            });

            const snapshotIdMap = {};
            snapshotProfiles.forEach((profile) => {
              const spId = profile._id;
              if (profile.tenantid) {
                const tid =
                  profile.tenantid?._id ??
                  profile.tenantid?.id ??
                  profile.tenantid?.tenant_id;
                if (tid) snapshotIdMap[tid] = spId;
                (profile.tenantid?.gurantor_id || []).forEach((g) => {
                  if (g?._id) snapshotIdMap[g._id] = spId;
                });
              }
              (profile.application_id?.participants || []).forEach((p) => {
                const pt = p.tenant_info;
                if (pt?._id) snapshotIdMap[pt._id] = spId;
                (pt?.gurantor_id || []).forEach((g) => {
                  if (g?._id) snapshotIdMap[g._id] = spId;
                });
              });
            });
            if (Object.keys(snapshotIdMap).length) {
              setIdToSharedProfileId((prev) => ({ ...prev, ...snapshotIdMap }));
            }

            const effectiveSelectionIds =
              selectionSelectedIds || backendSelectionIds;
            if (effectiveSelectionIds?.length) {
              rawTenants = rawTenants.filter((t) =>
                effectiveSelectionIds.includes(t?.id ?? t?.tenant_id ?? t?._id)
              );
            }
          } catch (snapshotError) {
            console.error(
              "[LinkedReportOverview] snapshot fetch failed, falling back to live data",
              snapshotError
            );
          }
        }

        if (rawTenants.length === 0) {
          const tenantRes = await getTenantExtraData(effectiveSharedProfileIds);
          rawTenants = tenantRes?.success_res ?? [];
          const effectiveSelectionIds =
            selectionSelectedIds || backendSelectionIds;
          if (effectiveSelectionIds?.length) {
            rawTenants = rawTenants.filter((t) =>
              effectiveSelectionIds.includes(t?.id ?? t?.tenant_id ?? t?._id)
            );
          }
        }

        await hydrateReportStatuses(rawTenants);
        setProcessingApplicants(
          getProcessingReportApplicants(rawTenants, buildDisplayLabel).map(
            (applicant) =>
              urlGuarantorIds.includes(String(applicant?._id))
                ? { ...applicant, role: "guarantor" }
                : applicant
          )
        );
        // Keep processing cards separate from the decision/API payload.
        const decisionPeople = getReportDecisionPeople(rawTenants);

        // Participants flagged as guarantors (via `guarantorid=` URL param)
        // are stored as tenant participants in selectionModel. Split them out
        // so they score and display as guarantors. Nested guarantors
        // (tenant.gurantor_id) are unaffected — their ids never match a
        // decision person id, so existing behaviour is preserved when the
        // param is absent.
        const urlGuarantorIdSet = new Set(
          (urlGuarantorIds || []).map((id) => String(id))
        );
        const getDecisionPersonId = (t) =>
          String(t?._id ?? t?.id ?? t?.tenant_id ?? "");
        const tenantDecisionPeople = decisionPeople.filter(
          (t) => !urlGuarantorIdSet.has(getDecisionPersonId(t))
        );
        const guarantorParticipantPeople = decisionPeople.filter((t) =>
          urlGuarantorIdSet.has(getDecisionPersonId(t))
        );

        // Persist raw tenants for KYC checks (snapshot or live)
        {
          const map = {};
          rawTenants.forEach((t) => {
            const ids = [t?._id, t?.id, t?.tenant_id].filter(Boolean);
            ids.forEach((id) => {
              map[id] = t;
              map[String(id)] = t;
            });
          });
          setRawTenantsById(map);
        }

        // ── Compute incomes & guarantor scores ──
        const guarantorDataArr = [];

        const incomesData = await Promise.all(
          tenantDecisionPeople.map(async (tenant) => {
            const incomeSelection = resolveIncomeSelection(
              tenant?.income_selection,
              tenant?.id ?? tenant?.tenant_id ?? tenant?._id
            );
            const tenantIncome = isReportPersonProcessing(tenant)
              ? 0
              : getIncomeForRecord(
                  tenant,
                  tenant?.income_selection?.tenant_source,
                  tenant?.custom_income_sources,
                  getSavedIncomeComponents(tenant?.income_selection, {
                    tenantId: tenant?._id,
                  })
                );
            tenant._localIncome = tenantIncome;

            const guarantors = tenant?.gurantor_id || [];
            const guarantorIncomeSelection =
              incomeSelection?.[tenant?._id]?.guarantor_sources ??
              incomeSelection?.guarantor_sources;

            const guarantorResults = await Promise.all(
              guarantors.map(async (g) => {
                const guarantorIncomeObj = getCustomIncomeDetails(
                  g,
                  user,
                  _t,
                  guarantorIncomeSelection?.[g?._id],
                  undefined,
                  undefined,
                  deaWorkspace,
                  tenant?.custom_income_sources,
                  getSavedIncomeComponents(tenant?.income_selection, {
                    tenantId: tenant?._id,
                    userId: g?._id,
                    isGuarantor: true,
                  })
                );
                const guarantorIncome = guarantorIncomeObj?.amount ?? 0;
                g._localIncome = guarantorIncome;

                const guarantorOnlyEffortRate =
                  guarantorIncome > 0
                    ? (getRentPercentage(guarantorIncome, effectiveRent)
                        ?.roundValue ?? 0)
                    : 0;

                const gTemp = await getFieldForPolicies(
                  g,
                  user,
                  guarantorOnlyEffortRate,
                  giveInsurance,
                  deaWorkspace
                );
                const gErrors = Object.entries(gTemp).filter(
                  ([, value]) => value?.isHardFail === true
                );
                if (gTemp?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE !== undefined) {
                  gTemp.EFFORT_RATE_SCORE =
                    gTemp.EFFORT_RATE_SCORE.EFFORT_RATE_SCORE;
                }
                const gScoreResult = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );
                const guarantorScore = gScoreResult?.finalScore ?? 0;
                const weight = getGuarantorWeight(guarantorScore);

                const label = buildDisplayLabel(g);
                gTemp.label = label;

                const result = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );
                result.errorEntries = gErrors;
                result.isHardFail = gErrors?.length > 0;

                const guarantorTenantData = {
                  documentNumber: getIdentityDetails(g, g?.onfido_user_kyc)
                    ?.value,
                  dob: tenant?.dob,
                  income: g._localIncome,
                  is_outdated: !!g?.is_outdated,
                  lastUpdatedAt:
                    g?.profile_renewed_at ||
                    g?.profile_completed_at ||
                    g?.created_at ||
                    null,
                };

                guarantorDataArr.push({
                  _id: g._id,
                  data: {
                    ...result,
                    vals: gTemp,
                    _id: g._id,
                    tenantData: guarantorTenantData,
                    label,
                    isGuarantor: true,
                    role: "guarantor",
                  },
                });

                return guarantorIncome * weight;
              })
            );

            // effort rate type decides whose income counts
            const effortRateType = getEffortRateType(tenant, tenant);
            return {
              tenantIncome: ["tenant", "both"].includes(effortRateType)
                ? tenantIncome
                : 0,
              guarantorIncomeSum: ["guarantor", "both"].includes(effortRateType)
                ? guarantorResults.reduce((sum, val) => sum + val, 0)
                : 0,
            };
          })
        );

        // Application participants flagged as guarantors via `guarantorid=`
        // URL param: score as guarantors (same weighting as nested guarantors)
        // and count their income as guarantor income.
        const guarantorParticipantIncomes = await Promise.all(
          guarantorParticipantPeople
            .filter((tenant) => !isReportPersonProcessing(tenant))
            .map(async (tenant) => {
              const participantIncome = getIncomeForRecord(
                tenant,
                tenant?.income_selection?.tenant_source,
                tenant?.custom_income_sources,
                getSavedIncomeComponents(tenant?.income_selection, {
                  tenantId: tenant?._id,
                })
              );
              tenant._localIncome = participantIncome;

              const participantEffortRate =
                participantIncome > 0
                  ? (getRentPercentage(participantIncome, effectiveRent)
                      ?.roundValue ?? 0)
                  : 0;
              const gTemp = await getFieldForPolicies(
                tenant,
                user,
                participantEffortRate,
                giveInsurance,
                deaWorkspace
              );
              const gErrors = Object.entries(gTemp).filter(
                ([, value]) => value?.isHardFail === true
              );
              if (gTemp?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE !== undefined) {
                gTemp.EFFORT_RATE_SCORE =
                  gTemp.EFFORT_RATE_SCORE.EFFORT_RATE_SCORE;
              }
              const gScoreResult = await calculateFinalScore(
                customPolicyDetails.scoringConfig,
                customPolicyDetails.overlayConfig,
                customPolicyDetails.policyConfig,
                gTemp
              );
              const weight = getGuarantorWeight(gScoreResult?.finalScore ?? 0);
              const label = buildDisplayLabel(tenant);
              gTemp.label = label;
              gScoreResult.errorEntries = gErrors;
              gScoreResult.isHardFail = gErrors?.length > 0;
              guarantorDataArr.push({
                _id: tenant?._id,
                data: {
                  ...gScoreResult,
                  vals: gTemp,
                  _id: tenant?._id,
                  tenantData: {
                    documentNumber: getIdentityDetails(
                      tenant,
                      tenant?.onfido_user_kyc
                    )?.value,
                    dob: tenant?.dob,
                    income: tenant._localIncome,
                    is_outdated: !!tenant?.is_outdated,
                    lastUpdatedAt:
                      tenant?.profile_renewed_at ||
                      tenant?.profile_completed_at ||
                      tenant?.created_at ||
                      null,
                  },
                  label,
                  isGuarantor: true,
                  role: "guarantor",
                },
              });
              return participantIncome * weight;
            })
        );

        const tenantIncomeSum = incomesData.reduce(
          (sum, data) => sum + data.tenantIncome,
          0
        );
        const nestedGuarantorIncomeSum = incomesData.reduce(
          (sum, data) => sum + data.guarantorIncomeSum,
          0
        );
        const participantGuarantorIncomeSum =
          guarantorParticipantIncomes.reduce((sum, val) => sum + val, 0);
        const guarantorIncomeSum =
          nestedGuarantorIncomeSum + participantGuarantorIncomeSum;
        const totalIncome = tenantIncomeSum + guarantorIncomeSum;
        const finalEffortRate =
          getRentPercentage(totalIncome, effectiveRent)?.roundValue ?? "0";

        setCombinedIncome(totalIncome);
        setCombinedEffortRate(
          typeof finalEffortRate === "number"
            ? `${finalEffortRate}%`
            : "No data"
        );

        // ── Score tenants ──
        const processedTenants = await Promise.all(
          tenantDecisionPeople
            .filter((tenant) => !isReportPersonProcessing(tenant))
            .map(async (tenant) => {
              const temp = await getFieldForPolicies(
                tenant,
                user,
                finalEffortRate,
                giveInsurance,
                deaWorkspace
              );
              const errorEntries = Object.entries(temp).filter(
                ([, value]) => value?.isHardFail === true
              );

              let guarantorOutsideSpain = false;
              for (const g of tenant?.gurantor_id || []) {
                const forensicData =
                  g?.tenant_extra_field_id?.forensic_financial_data;
                if (!forensicData) continue;
                for (const key in forensicData) {
                  if (
                    forensicData[key]?.origin &&
                    forensicData[key]?.origin !== "Local"
                  ) {
                    guarantorOutsideSpain = true;
                    break;
                  }
                }
                if (guarantorOutsideSpain) break;
              }
              if (
                guarantorOutsideSpain &&
                isGuarantorOutsideSpainHardFail(user, deaWorkspace)
              ) {
                errorEntries.push([
                  "GUARANTOR_OUTSIDE_SPAIN",
                  { isHardFail: true, label: "guarantorLivingOutsideSpain" },
                ]);
              }

              const label = buildDisplayLabel(tenant);
              tenant.label = label;
              temp.EFFORT_RATE_SCORE =
                temp?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE;

              const result = await calculateFinalScore(
                customPolicyDetails.scoringConfig,
                customPolicyDetails.overlayConfig,
                customPolicyDetails.policyConfig,
                temp
              );
              result.errorEntries = errorEntries;
              result.isHardFail = errorEntries?.length > 0;

              const tenantDataObj = {
                documentNumber: getIdentityDetails(
                  tenant,
                  tenant?.onfido_user_kyc
                )?.value,
                dob: tenant?.dob,
                income: tenant._localIncome,
                displayEffortRate: getRentPercentage(
                  tenant._localIncome,
                  effectiveRent
                )?.roundValue,
                is_outdated: !!tenant?.is_outdated,
                lastUpdatedAt:
                  tenant?.profile_renewed_at ||
                  tenant?.profile_completed_at ||
                  tenant?.created_at ||
                  null,
              };

              return {
                tenantId: tenant?._id,
                tenantData: {
                  ...result,
                  vals: temp,
                  _id: tenant?._id,
                  tenantData: tenantDataObj,
                  label: tenant?.label ?? "",
                  isGuarantor: false,
                  role: "tenant",
                },
              };
            })
        );

        const tempTenantResults = {};
        const guarantorResults = {};
        processedTenants.forEach((pt) => {
          if (pt.tenantId) tempTenantResults[pt.tenantId] = pt.tenantData;
        });
        guarantorDataArr.forEach((gData) => {
          guarantorResults[gData._id] = gData.data;
        });

        const selectedDecisionPeople = tenantDecisionPeople.filter(
          (tenant) => !isReportPersonProcessing(tenant)
        );
        const selectedForDecision = selectedDecisionPeople.length
          ? selectedDecisionPeople
          : Object.values(guarantorResults);
        setCalculationInputs({
          tenantResults: tempTenantResults,
          selectedTenant: selectedForDecision,
          guarantorResults,
          finalEffortRate,
        });

        if (
          !Object.keys(tempTenantResults).length &&
          !Object.keys(guarantorResults).length
        ) {
          const pendingReport = {
            report: {
              applicants: [],
              case: { decision: "processingProfile", recommendation: [] },
            },
          };
          setLocalReport(pendingReport);
          setApiReport(pendingReport.report);
          setDecisionReasons([]);
          setStatus("review");
          setCombinedEffortRate(_t("noData", "No data"));
          setReportLoading("loaded");
          return;
        }

        // ── Local decision (computeFinalResult) ──
        const { result: localReportResult, sideEffects } = computeFinalResult({
          tenantResults: tempTenantResults,
          selectedTenant: selectedForDecision,
          customPolicyDetails,
          giveInsurance: false,
          finalEffortRate,
          enabled: false,
          deaWorkspace,
          user,
          lastVerdict,
          lastRecommendationFlags,
          STATUS_STYLES,
          translate: _t,
          isReport: true,
          guarantorResults,
          aggregationStrategy: aggregationStrategyRef.current,
        });

        if (sideEffects.verdict) {
          setLastVerdict(sideEffects.verdict);
          if (sideEffects.verdict === "Approved") setStatus("approved");
          else if (sideEffects.verdict === "Rejected") setStatus("declined");
          else setStatus("review");
        }
        if (sideEffects.recommendationFlags)
          setLastRecommendationFlags(sideEffects.recommendationFlags);
        if (localReportResult?.report?.recommendation)
          setDecisionReasons(localReportResult.report.recommendation);
        if (localReportResult) setLocalReport(localReportResult);

        // ── Enhanced report via API ──
        if (localReportResult) {
          const apiResponse = await getRiskAssessmentReport(localReportResult);
          if (apiResponse?.data?.report) {
            setApiReport(apiResponse.data.report);
            setReportLoading("loaded");
          } else {
            setReportLoading("failed");
            setApiReport(localReportResult.report);
          }
        } else {
          setReportLoading("failed");
        }
      } catch (err) {
        console.log("[LinkedReportOverview] error", err);
        setReportLoading("failed");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [customPolicyDetails, cachedReportState, user, processingRefreshKey]); // eslint-disable-line

  // Only live, unresolved people need polling. Historical snapshots cannot
  // change, and completed profiles never need another status request.
  useEffect(() => {
    if (loading || awardSnapshotCompleted || historyEventId) return;
    const pendingIds = [
      ...new Set(processingApplicants.map((a) => a._id)),
    ].filter(haveValue);
    if (!pendingIds.length) return;

    let cancelled = false;
    let timeoutId;
    const controller = new AbortController();
    const schedule = () => {
      timeoutId = window.setTimeout(checkStatuses, 15000);
    };
    const checkStatuses = async () => {
      // Background tabs should not keep making network requests.
      if (document.visibilityState === "hidden") {
        schedule();
        return;
      }
      const statuses = await Promise.all(
        pendingIds.map(async (id) => {
          try {
            const response = await axiosInstance.get(
              `${process.env.REACT_APP_SERVER_URL}/tenants/checkForensicAnalysisStatus?tenant_id=${encodeURIComponent(id)}&fresh=false`,
              { signal: controller.signal, timeout: 10000 }
            );
            return response?.data?.success_res?.forensic_status;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      if (statuses.includes("COMPLETED")) {
        // Fetch fresh assessment data and bypass the original report cache.
        setProcessingRefreshKey((key) => key + 1);
      } else {
        // Schedule after completion so slow requests cannot overlap.
        schedule();
      }
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [loading, processingApplicants, awardSnapshotCompleted, historyEventId]);

  useEffect(() => {
    getPolicyDetails();
  }, []);

  // // Debug — keep commented for troubleshooting
  // console.log("localReport", localReport);

  // ── Applicants & derived UI state ───────────────────────────

  const getApplicants = useCallback(() => {
    if (apiReport?.applicants) {
      return apiReport.applicants.map((a) => {
        const applicant = localReport?.report?.applicants?.find(
          (la) => la._id === a._id
        );
        if (!applicant) return a;
        return {
          ...applicant,
          ...a,
          vals: { ...(applicant.vals || {}), ...(a.vals || {}) },
          errorEntries: a.errorEntries?.length
            ? a.errorEntries
            : applicant.errorEntries || [],
          isHardFail: a.isHardFail ?? applicant.isHardFail,
          tenantData: { ...applicant.tenantData, ...a.tenantData },
        };
      });
    }
    if (localReport?.report?.applicants) return localReport.report.applicants;
    return [];
  }, [apiReport, localReport]);

  const applicants = useMemo(() => getApplicants(), [getApplicants]);
  // console.log("applicants", applicants);

  const hardFailWarnings = useMemo(
    () => buildHardFailWarnings(applicants),
    [applicants]
  );

  const outdatedApplicants = useMemo(
    () => applicants.filter((a) => a?.tenantData?.is_outdated),
    [applicants]
  );

  const outdatedTenants = useMemo(
    () =>
      outdatedApplicants.map((a) => ({
        _id: a._id,
        fname: a.name,
        forensic_name: a.name,
        role: a.role || "tenant",
        reminderSentAt: remindersByTenant[a._id] || null,
        profile_renewed_at: a.tenantData?.lastUpdatedAt || null,
      })),
    [outdatedApplicants, remindersByTenant]
  );

  useEffect(() => {
    if (!outdatedApplicants.length) {
      setReminderSentAt(null);
      setRemindersByTenant({});
      return;
    }

    let cancelled = false;
    Promise.all(
      outdatedApplicants.map(async (applicant) => {
        try {
          const response = await getProfileReminders(applicant._id);
          return {
            id: applicant._id,
            sentAt: response?.data?.[0]?.sent_at || null,
          };
        } catch {
          return { id: applicant._id, sentAt: null };
        }
      })
    ).then((reminders) => {
      if (cancelled) return;
      setRemindersByTenant(
        Object.fromEntries(
          reminders
            .filter((item) => item.sentAt)
            .map((item) => [item.id, item.sentAt])
        )
      );
      const latestSentAt =
        reminders
          .map((item) => item.sentAt)
          .filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0] || null;
      setReminderSentAt(latestSentAt);
    });

    return () => {
      cancelled = true;
    };
  }, [outdatedApplicants]);

  useEffect(() => {
    if (!applicants?.length) {
      setProfiles([]);
      return;
    }
    setProfiles(
      applicants.map((applicant) => ({
        role: applicant?.role || "tenant",
        isInsurable: applicant?.isInsurable !== false,
      }))
    );
  }, [applicants]);

  // ── Callbacks ───────────────────────────────────────────────

  const handleAggregationStrategyChange = useCallback(
    (strategy) => {
      aggregationStrategyRef.current = strategy;
      setAggregationStrategy(strategy);

      // This is deliberately local: all pillar values have already been
      // calculated, so changing aggregation never needs another API request.
      const inputs =
        calculationInputs ||
        (() => {
          const fallbackApplicants = localReport?.report?.applicants || [];
          const toComputationShape = (applicant) => ({
            ...applicant,
            finalScore: applicant.finalScore,
            pillars: {
              INCOME_AFFORDABILITY: applicant.pillars?.incomeAffordability,
              EMPLOYMENT_STABILITY: applicant.pillars?.employmentStability,
              FINANCIAL_BEHAVIOUR: applicant.pillars?.financialBehaviour,
              CREDIT_DEFAULT_RISK: applicant.pillars?.creditDefaultRisk,
              DOCUMENTATION_STRENGTH: applicant.pillars?.documentationStrength,
            },
          });
          const tenantResults = Object.fromEntries(
            fallbackApplicants
              .filter((applicant) => applicant?.role !== "guarantor")
              .map((applicant) => [
                applicant._id,
                toComputationShape(applicant),
              ])
          );
          // Guarantors are full applicants for aggregation, so they need the
          // same computation shape (UPPERCASE pillar keys) as tenants.
          const guarantorResults = Object.fromEntries(
            fallbackApplicants
              .filter((applicant) => applicant?.role === "guarantor")
              .map((applicant) => [
                applicant._id,
                toComputationShape(applicant),
              ])
          );
          return {
            tenantResults,
            selectedTenant: fallbackApplicants.filter(
              (a) => a?.role !== "guarantor"
            ),
            guarantorResults,
            finalEffortRate:
              Number.parseFloat(String(combinedEffortRate).replace("%", "")) ||
              0,
          };
        })();

      if (
        !Object.keys(inputs?.tenantResults || {}).length &&
        !Object.keys(inputs?.guarantorResults || {}).length
      )
        return;

      const { result, sideEffects } = computeFinalResult({
        ...inputs,
        customPolicyDetails,
        giveInsurance: false,
        enabled: false,
        deaWorkspace,
        user,
        lastVerdict,
        lastRecommendationFlags,
        STATUS_STYLES,
        translate: _t,
        isReport: true,
        aggregationStrategy: strategy,
      });

      if (result) {
        setLocalReport(result);
        setDecisionReasons(result.report?.case?.recommendation || []);
        const nextDecision = result.report?.case?.decision;
        setApiReport((currentReport) =>
          currentReport
            ? {
                ...currentReport,
                decision: nextDecision,
                decisionSummary: null,
              }
            : result.report
        );
        setStatus(
          nextDecision === "Approved"
            ? "approved"
            : nextDecision === "Rejected"
              ? "declined"
              : "review"
        );
      }
      if (sideEffects?.verdict) setLastVerdict(sideEffects.verdict);
      if (sideEffects?.recommendationFlags)
        setLastRecommendationFlags(sideEffects.recommendationFlags);
    },
    [
      calculationInputs,
      localReport,
      combinedEffortRate,
      customPolicyDetails,
      deaWorkspace,
      user,
      lastVerdict,
      lastRecommendationFlags,
      _t,
    ]
  );

  const retryApiCall = () => {
    if (!localReport) return;
    setReportLoading("loading");
    getRiskAssessmentReport(localReport)
      .then((response) => {
        if (response?.data?.report) {
          setApiReport(response.data.report);
          setReportLoading("loaded");
        } else {
          setReportLoading("failed");
        }
      })
      .catch((error) => {
        console.log("API retry error:", error);
        setReportLoading("failed");
      });
  };

  const downloadReportPdf = async () => {
    if (!document.getElementById("divToPrint")) return;

    let container = null;
    let hiddenElements = [];
    const previousDisplays = new Map();

    try {
      setShowSpinner(true);
      setIsPdfExporting(true);

      // Wait for the export-only render so all document names are in the DOM.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );

      container = document.getElementById("divToPrint");
      if (!container) return;

      hiddenElements = Array.from(document.querySelectorAll(".no-print"));

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const PDF_MARGIN = 24;

      container.classList.add("pdf-export");
      hiddenElements.forEach((el) => {
        previousDisplays.set(el, el.style.display);
        el.style.display = "none";
      });

      const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "var(--surface)",
        scale: 2,
        windowWidth: container.scrollWidth,
        windowHeight: container.scrollHeight,
        scrollY: -window.scrollY,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const pageWidth = A4_WIDTH - PDF_MARGIN * 2;
      const pageHeight = A4_HEIGHT - PDF_MARGIN * 2;

      if (!canvas.width || !canvas.height) {
        throw new Error("Unable to capture an empty report canvas");
      }

      const pageSliceHeight = Math.max(
        1,
        Math.floor((pageHeight * canvas.width) / pageWidth)
      );
      let sourceY = 0;

      while (sourceY < canvas.height) {
        const sliceHeight = Math.min(pageSliceHeight, canvas.height - sourceY);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const pageContext = pageCanvas.getContext("2d");
        if (!pageContext) {
          throw new Error("Unable to create a PDF page canvas");
        }
        pageContext.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        if (sourceY > 0) pdf.addPage();
        pdf.addImage(
          pageCanvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          PDF_MARGIN,
          PDF_MARGIN,
          pageWidth,
          (sliceHeight * pageWidth) / canvas.width
        );
        sourceY += sliceHeight;
      }

      const addressParts = [
        addressVal?.street,
        addressVal?.street_number,
        addressVal?.floor_number,
        addressVal?.flat_number,
        addressVal?.postcode,
        addressVal?.muncipality,
      ]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, "_");

      const tenantNames = applicants
        .filter((applicant) => applicant.role !== "guarantor")
        .map((applicant) => (applicant.name || "").replace(/\s+/g, "_"))
        .join("_");

      const filename = `${addressParts}${tenantNames ? `_${tenantNames}` : ""}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error("Error capturing element:", err);
    } finally {
      hiddenElements.forEach((el) => {
        el.style.display = previousDisplays.get(el) ?? "";
      });
      container?.classList.remove("pdf-export");
      setIsPdfExporting(false);
      setShowSpinner(false);
    }
  };

  const getProfileIsResolved = useCallback(
    async (profileId) => {
      if (!haveValue(profileId)) return null;
      try {
        const res = await getProfileResolutionStatus(profileId, "documents");
        return res?.isResolved ? res : null;
      } catch (err) {
        console.log("Failed to fetch profile resolution status", err);
        return null;
      }
    },
    [getProfileResolutionStatus]
  );

  const formatMissingDoc = (documentName) => {
    if (!documentName) return "";
    const payslipMatch = documentName.match(/^payslip\s*-\s*(.+)$/i);
    if (payslipMatch) return `${_t("Payslip")} - ${payslipMatch[1]}`;
    return _t(documentName);
  };

  const requestUpdateTenants = useMemo(
    () =>
      applicants.map((a) => {
        const initials = (a.name || "")
          .split(" ")
          .filter(Boolean)
          .map((n) => n[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();
        const missingDocs = normalizeMissingDocuments(
          a?.subScores?.documentation?.missingDocs || [],
          currentLang
        ).map(formatMissingDoc);
        return {
          _id: a._id,
          name: a.name,
          initials,
          role: a.role || "tenant",
          missingDocs,
        };
      }),
    [applicants, currentLang]
  );

  const handleOpenOutdatedModal = () => {
    if (outdatedTenants.length > 0) setShowOutdatedModal(true);
  };

  const handleCloseOutdatedModal = () => setShowOutdatedModal(false);

  const handleSendRequestUpdate = async ({
    tenantIds,
    selectedItems,
    note,
  }) => {
    if (!tenantIds?.length) return false;
    if (!selectedItems?.length) {
      toast.error(
        _t(
          "pleaseSelectAtLeastOneSection",
          "Please select at least one section"
        )
      );
      return false;
    }
    try {
      setIsSendingFeedback(true);
      await Promise.all(
        tenantIds.map((tenantId) =>
          sendNoteToTenant({ tenant_id: tenantId, selectedItems, note })
        )
      );
      toast.success(
        _t("feedbackSentSuccessfully", "Feedback sent successfully")
      );
      setShowRequestUpdateModal(false);
      return true;
    } catch (error) {
      console.log("[LinkedReportOverview] send feedback error", error);
      toast.error(_t("FailedToSendNote", "Failed to send feedback"));
      return false;
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleSendReminder = async (
    selectedIds = outdatedTenants.map((t) => t._id)
  ) => {
    const selectedTenants = outdatedTenants.filter((tenant) =>
      selectedIds.includes(tenant._id)
    );
    if (!selectedTenants.length) return false;

    try {
      setIsSendingReminder(true);
      await Promise.all(
        selectedTenants.map((t) => createProfileReminder(t._id))
      );
      const sentAt = new Date().toISOString();
      setRemindersByTenant((current) =>
        selectedIds.reduce((next, id) => ({ ...next, [id]: sentAt }), current)
      );
      setReminderSentAt(sentAt);
      toast.success(
        _t("profileUpdateReminderSent", "Profile update reminder sent")
      );
      return true;
    } catch (error) {
      console.log("[LinkedReportOverview] send reminder error", error);
      return false;
    } finally {
      setIsSendingReminder(false);
    }
  };

  const toggleResolveTenant = (tenantId) => {
    if (!tenantId) return;
    setSelectedResolveTenantIds((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleApplyResolutions = async (note) => {
    if (!hasPendingChanges) return;
    setResolvingDocs(true);
    try {
      const ops = [];
      pendingResolveIds.forEach((tenantId) =>
        ops.push(
          markProfileResolved({ tenant_id: tenantId, type: "documents", note })
        )
      );
      pendingUnresolveIds.forEach((tenantId) =>
        ops.push(unmarkProfileResolved(tenantId, "documents"))
      );
      await Promise.all(ops);

      if (pendingResolveIds.length && pendingUnresolveIds.length)
        toast.success(_t("documentsUpdated", "Documents updated"));
      else if (pendingUnresolveIds.length)
        toast.success(_t("documentsUnresolved", "Documents unresolved"));
      else
        toast.success(
          _t("documentsMarkedResolved", "Documents marked resolved")
        );

      setResolveNote("");
      setShowResolveNoteModal(false);
      setDataRefreshKey((currentKey) => currentKey + 1);
    } catch (err) {
      console.log(err, "Error applying document resolutions");
      toast.error(_t("failedToUpdate", "Failed to update"));
    } finally {
      setResolvingDocs(false);
    }
  };

  // ── Forensic processing lookup (tenants + nested guarantors) ──
  // Statuses are resolved before assessment; only COMPLETED people can open.
  // Processing cards stay separate from the eligible report applicants.
  const rawPersonById = useMemo(() => {
    const map = { ...(rawTenantsById || {}) };
    Object.values(rawTenantsById || {}).forEach((tenant) => {
      (tenant?.gurantor_id || []).forEach((guarantor) => {
        if (guarantor?._id && !map[guarantor._id]) {
          map[guarantor._id] = guarantor;
          map[String(guarantor._id)] = guarantor;
        }
      });
    });
    return map;
  }, [rawTenantsById]);

  const isPersonProcessing = useCallback(
    (personId) => {
      const raw = rawPersonById[String(personId)];
      return raw ? isReportPersonProcessing(raw) : false;
    },
    [rawPersonById]
  );

  const handleProfileClick = useCallback(
    (profile) => {
      const sharedProfileId =
        profile.sharedProfileId ||
        profile.sharedProfile_id ||
        idToSharedProfileId[profile.id] ||
        idToSharedProfileId[profile._id];
      if (!sharedProfileId) return;
      if (
        profile?.isProcessing ||
        isPersonProcessing(profile?.id || profile?._id)
      )
        return;

      const baseUrl = `/${workspaceId}/profile-property?sharedprofileid=${sharedProfileId}`;
      const suffix =
        profile.role === "tenant"
          ? `&tenant_id=${profile.id}`
          : `&guarantor=${profile.id}`;
      let url = `${baseUrl}${suffix}`;
      if (isSkorSdk) url += "&app_type=skor-sdk";

      // In skor-sdk iframe keep navigation inside current window; otherwise open new tab
      window.open(url, isSkorSdk ? "_self" : "_blank");
    },
    [workspaceId, idToSharedProfileId, isSkorSdk, isPersonProcessing]
  );

  // ── Missing-docs warnings ───────────────────────────────────
  useEffect(() => {
    if (!applicants?.length) {
      setIndividualWarnings({});
      setIndividualResolutions({});
      setIsDocsWarningsLoading(false);
      return;
    }

    let cancelled = false;
    setIsDocsWarningsLoading(true);

    const build = async () => {
      const warnings = {};
      const resolutions = {};

      await Promise.all(
        applicants.map(async (applicant) => {
          const rawMissingDocs =
            applicant?.subScores?.documentation?.missingDocs ||
            applicant?.vals?.MISSING_DOCS ||
            [];
          const missingDocs = normalizeMissingDocuments(
            rawMissingDocs,
            currentLang
          );
          if (!missingDocs.length) return;

          const resolution = await getProfileIsResolved(applicant._id);
          if (cancelled) return;
          if (resolution) resolutions[applicant._id] = resolution;
          warnings[applicant._id] = {
            label: applicant.name || applicant.label || applicant._id,
            docsMissing: missingDocs,
            isResolved: !!resolution,
          };
        })
      );

      if (!cancelled) {
        setIndividualWarnings(warnings);
        setIndividualResolutions(resolutions);
        setIsDocsWarningsLoading(false);
      }
    };

    build();
    return () => {
      cancelled = true;
    };
  }, [applicants, dataRefreshKey, getProfileIsResolved, currentLang]);

  // ── KYC unclear categories (tenant only) ─────────────────────
  useEffect(() => {
    const tenantsOnly = applicants.filter((a) => a?.role !== "guarantor");
    if (!tenantsOnly.length) {
      setKycWarnings({});
      setIsKycWarningsLoading(false);
      return;
    }
    let cancelled = false;
    setIsKycWarningsLoading(true);
    const build = async () => {
      const warnings = {};
      try {
        await Promise.all(
          tenantsOnly.map(async (applicant) => {
            const tenantId = applicant?._id;
            if (!tenantId) return;
            const raw =
              rawTenantsById[tenantId] ||
              rawTenantsById[String(tenantId)] ||
              null;
            const userObj = raw || applicant;
            const kycUser = userObj?.onfido_user_kyc;
            // Only KYC records with check results can have unresolved items.
            if (!kycUser?.kyc_checks && !kycUser?.kyc_aml) return;
            let kycRes = null;
            try {
              kycRes = await getProfileResolutionStatus(tenantId, "kyc");
            } catch (e) {
              console.log("Failed to fetch KYC status for", tenantId, e);
              kycRes = null;
            }
            if (cancelled) return;
            const kycStatus = getKycResolutionStatus(kycUser, kycRes);
            const unresolved = kycStatus.categoryResults.nonClear.filter(
              (c) => !kycStatus.resolvedCategoryKeys.has(c.key)
            );
            if (unresolved.length === 0) return;
            warnings[tenantId] = {
              label: applicant.name || applicant.label || tenantId,
              unresolved,
              unresolvedCount: unresolved.length,
              tenantId,
            };
          })
        );
      } catch (e) {
        console.log("Failed to build KYC warnings", e);
      } finally {
        if (!cancelled) {
          setKycWarnings(warnings);
          setIsKycWarningsLoading(false);
        }
      }
    };
    build();
    return () => {
      cancelled = true;
    };
  }, [applicants, rawTenantsById, dataRefreshKey, getProfileResolutionStatus]);

  const handleKycTenantClick = useCallback(
    (tenantId) => {
      const spId =
        idToSharedProfileId[tenantId] || idToSharedProfileId[String(tenantId)];
      if (!spId || !tenantId) return;
      if (isPersonProcessing(tenantId)) return;
      const base = `/${workspaceId}/profile-property?sharedprofileid=${spId}&tenant_id=${tenantId}&kycAml=true`;
      let url = base;
      if (isSkorSdk) url += "&app_type=skor-sdk";
      window.open(url, isSkorSdk ? "_self" : "_blank", "noopener,noreferrer");
    },
    [idToSharedProfileId, workspaceId, isSkorSdk, isPersonProcessing]
  );

  const pendingResolveIds = useMemo(
    () =>
      selectedResolveTenantIds.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      ),
    [selectedResolveTenantIds, individualWarnings]
  );

  const pendingUnresolveIds = useMemo(() => {
    const selectedSet = new Set(selectedResolveTenantIds);
    return Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id)
      .filter((id) => !selectedSet.has(id));
  }, [selectedResolveTenantIds, individualWarnings]);

  const hasPendingChanges =
    pendingResolveIds.length > 0 || pendingUnresolveIds.length > 0;

  useEffect(() => {
    const resolvedIds = Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id);

    setSelectedResolveTenantIds((prev) => {
      const prevPendingResolve = prev.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      );
      return [...new Set([...resolvedIds, ...prevPendingResolve])];
    });
  }, [individualWarnings]);

  const resolveActionLabel = useMemo(() => {
    const hasResolve = pendingResolveIds.length > 0;
    const hasUnresolve = pendingUnresolveIds.length > 0;
    if (hasResolve && hasUnresolve) return _t("update", "Update");
    if (hasUnresolve) return _t("unresolve", "Unresolve");
    return _t("markResolved", "Mark resolved");
  }, [pendingResolveIds, pendingUnresolveIds, _t]);

  const [expandedDocs, setExpandedDocs] = useState({});
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const toggleDocsExpanded = (id) =>
    setExpandedDocs((prev) => ({ ...prev, [id]: !prev[id] }));
  const blockingRows = useMemo(
    () =>
      buildBlockingRows(
        hardFailWarnings,
        _t,
        status === "declined",
        localReport?.report?.case?.recommendation ||
          apiReport?.recommendation ||
          apiReport?.case?.recommendation ||
          []
      ),
    [hardFailWarnings, status, localReport, apiReport, _t]
  );

  // ── Profile transformation ──────────────────────────────────
  const transformApplicantToProfile = (applicant) => {
    const initials = applicant.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    let sections = [];
    const documentation = {
      title: _t(PILLAR_TITLE_KEYS.documentationStrength),
      badge: { label: _t(PILLAR_NO_DATA_KEY), variant: "neutral" },
      rows: [],
      docs: normalizeMissingDocuments(
        applicant.subScores?.documentation?.missingDocs || [],
        currentLang
      ).map(formatMissingDoc),
    };

    const pillarToSubScoreKey = {
      incomeAffordability: "affordability",
      employmentStability: "employment stability",
      financialBehaviour: "financial behaviour",
      creditDefaultRisk: "default risk",
    };

    if (applicant.pillars) {
      Object.entries(applicant.pillars).forEach(([key, value]) => {
        const badge = getPillarBadgeFromScore(key, value);

        if (key === "documentationStrength") {
          documentation.title = _t(PILLAR_TITLE_KEYS.documentationStrength);
          documentation.badge = {
            label: _t(badge.labelKey),
            variant: badge.variant,
          };
          return;
        }

        const rows = [];
        const relevantSubScores =
          PILLAR_SUB_SCORES[pillarToSubScoreKey[key]] || [];

        relevantSubScores.forEach((subKey) => {
          const subScore = applicant.subScores?.[subKey];
          if (!subScore) return;

          const hasDisplayEffortRate =
            subKey === "effortRate" &&
            applicant?.tenantData?.displayEffortRate !== undefined &&
            applicant?.tenantData?.displayEffortRate !== null;

          rows.push({
            label: SUB_SCORE_TRANSLATIONS[subKey]?.[currentLang] || _t(subKey),
            value: hasDisplayEffortRate
              ? `${applicant.tenantData.displayEffortRate}%`
              : subScore.label || subScore.score || _t(PILLAR_NO_DATA_KEY),
            muted: !hasDisplayEffortRate && !subScore.label,
          });
        });

        sections.push({
          title: _t(PILLAR_TITLE_KEYS[key] || key),
          badge: { label: _t(badge.labelKey), variant: badge.variant },
          rows,
        });
      });
    }

    const sharedProfileId =
      applicant.sharedProfileId ||
      applicant.sharedProfile_id ||
      idToSharedProfileId[applicant._id] ||
      effectiveSharedProfileIdsState?.[0] ||
      null;

    const isProcessing =
      applicant.isProcessing || isPersonProcessing(applicant._id);

    return {
      id: applicant._id,
      initials,
      label: applicant.name,
      role: applicant.role || "tenant",
      sharedProfileId,
      isProcessing,
      documentNumber:
        applicant?.tenantData?.documentNumber ??
        (currentLang === "es" ? "Sin datos" : "No data"),
      dob: formatDateOfBirth(applicant?.tenantData?.dob, currentLang),
      income:
        applicant?.tenantData?.income ??
        (currentLang === "es" ? "Sin datos" : "No data"),
      isInsurable: applicant.isInsurable !== false,
      isOutdated: !!applicant?.tenantData?.is_outdated,
      lastUpdatedAt: applicant?.tenantData?.lastUpdatedAt || null,
      sections,
      documentation,
    };
  };

  const renderProcessingPlaceholder = () => (
    <div className="linked-report__processing-placeholder" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((section) => (
        <div className="linked-report__processing-section" key={section}>
          <Skeleton width="45%" height="var(--text-18)" />
          <Skeleton width="90%" height="var(--text-14)" />
          <Skeleton width="75%" height="var(--text-14)" />
          <Skeleton width="85%" height="var(--text-14)" />
        </div>
      ))}
    </div>
  );

  const renderCard = (profile) => {
    const canOpenProfile = profile.sharedProfileId && !profile.isProcessing;
    return (
      <article
        className={`linked-report__card${profile.isOutdated ? " linked-report__card--outdated" : ""}`}
        key={profile.id}
      >
        <div className="linked-report__card-header">
          <Avatar name={profile.label} size="lg" />
          <div style={{ flex: 1 }}>
            <div className="linked-report__person-name-row">
              <div
                className={`linked-report__person-name d-flex align-items-center gap-1${canOpenProfile ? " cursor-pointer" : ""}`}
                onClick={() => {
                  if (canOpenProfile) {
                    handleProfileClick(profile);
                  }
                }}
                style={
                  canOpenProfile ? { cursor: "pointer" } : { cursor: "default" }
                }
                role={canOpenProfile ? "button" : undefined}
                tabIndex={canOpenProfile ? 0 : undefined}
                onKeyDown={(event) => {
                  if (
                    canOpenProfile &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    handleProfileClick(profile);
                  }
                }}
              >
                <span>{profile.label}</span>
                {canOpenProfile && (
                  <img
                    src="/images/external-link.svg"
                    alt=""
                    className="no-print"
                    style={{
                      width: "0.833vw",
                      height: "0.833vw",
                      marginLeft: "0.347vw",
                      verticalAlign: "middle",
                    }}
                  />
                )}
              </div>
              {profile.isProcessing && (
                <Badge tone="neutral">
                  {_t("processingProfile", "Processing profile")}
                </Badge>
              )}
              {!profile.isProcessing && (
                <Badge
                  tone={profile.isInsurable ? "lime" : "warning"}
                  className="report-status-badge"
                >
                  {profile.isInsurable ? (
                    <Icon name="shieldCheck" size={16} />
                  ) : null}
                  {_t(profile.isInsurable ? "insurable" : "notInsurable")}
                </Badge>
              )}
            </div>
            {!profile.isProcessing && (
              <div className="linked-report__person-meta">
                {`${profile.documentNumber} • ${formatDateOfBirth(profile.dob, currentLang)} • ${_t("income")}: ${profile.income}`}
              </div>
            )}
          </div>
          {profile.isOutdated && (
            <Badge tone="warning">
              {_t("outdated")} ·{" "}
              {formatMonthYear(profile.lastUpdatedAt, currentLang)}
            </Badge>
          )}
        </div>

        {profile.isOutdated && (
          <div className="linked-report__outdated-note">
            <img
              src="/images/warning-dark-yellow.svg"
              alt=""
              className="linked-report__outdated-note-icon"
            />
            <span>
              {_t(
                "outdatedProfileReportNote",
                "Data from the last update ({{date}}). {{name}}'s situation may have changed.",
                undefined,
                {
                  date: profile.lastUpdatedAt
                    ? moment(profile.lastUpdatedAt)
                        .locale(currentLang === "es" ? "es" : "en-gb")
                        .format("D MMM YYYY")
                    : profile.created_at
                      ? moment(profile.created_at)
                          .locale(currentLang === "es" ? "es" : "en-gb")
                          .format("D MMM YYYY")
                      : currentLang === "es"
                        ? "sin fecha"
                        : "no date",
                  name: profile.label,
                }
              )}
            </span>
          </div>
        )}

        {profile.isProcessing ? (
          <div className="linked-report__processing-state">
            {renderProcessingPlaceholder()}
            <Notice
              layout="stack"
              className="linked-report__processing-message"
              title={_t("processingProfile", "Processing profile")}
            >
              {_t(
                "waitUntilProfileProcessingCompletes",
                "Please wait until processing is complete."
              )}
            </Notice>
          </div>
        ) : (
          <>
            <div className="linked-report__metrics-grid linked-report-overview__metrics-grid">
              {profile.sections.map((section) => (
                <PolicyMetricSection
                  key={`${profile.id}-${section.title}`}
                  title={section.title}
                  badge={section.badge}
                  rows={section.rows}
                  muted={profile.isOutdated}
                />
              ))}
            </div>

            <div className="linked-report__documentation">
              <div className="linked-report__metric-header">
                <h3>{profile.documentation.title}</h3>
                <Badge
                  tone={
                    profile.isOutdated
                      ? "neutral"
                      : getStatusBadgeTone(profile.documentation.badge.variant)
                  }
                >
                  {profile.documentation.badge.label}
                </Badge>
              </div>
              {profile.documentation.rows.map((row) => (
                <div
                  className="linked-report__row"
                  key={`${profile.id}-doc-${row.label}`}
                >
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}
              <ul className="linked-report__doc-list">
                {profile.documentation.docs.map((doc) => (
                  <li key={`${profile.id}-${doc}`}>{doc}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </article>
    );
  };

  const processingTenantNames = processingApplicants
    .filter((applicant) => applicant.role !== "guarantor")
    .map((applicant) => applicant.name);

  // ── Early returns (loading / error) ─────────────────────────

  if (loading && !processingTenantNames.length) return <Loader />;

  // Standalone /linked-report-overview (outside Homepage) hydrates user async — wait before showing report states
  if (!user && !cachedReportState) return <Loader />;

  if (reportLoading === "loading" && !processingTenantNames.length) {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--spinning">
              <svg
                className="spinner"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeOpacity="0.3"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeDasharray="80"
                  strokeLinecap="round"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 16 16"
                    to="360 16 16"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <p className="risk-report-state-title">{_t("generatingReport")}</p>
            <p className="risk-report-state-desc">
              {_t("wereAnalyzingTheApplicationThisMayTakeAFewSeconds")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (reportLoading === "failed" && !processingTenantNames.length) {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--error">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                stroke="var(--danger-text)"
                strokeWidth="2"
              >
                <circle cx="16" cy="16" r="14" />
                <path d="M10 16L14 20L22 12" />
              </svg>
            </div>
            <p className="risk-report-state-title">
              {_t("failedToLoadReport")}
            </p>
            <p className="risk-report-state-desc">
              {_t(
                "somethingWentWrongWhileGeneratingTheRiskAssessmentPleaseTryAgain"
              )}
            </p>
            <Button onClick={retryApiCall} iconStart={<Icon name="refresh" />}>
              {_t("retry")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────

  const decisionTitle =
    localReport?.report?.case?.decision || getDecisionLabel(apiReport, status);
  const displayApplicants = [...applicants, ...processingApplicants];
  const tenantCards = displayApplicants
    .filter((a) => a.role !== "guarantor")
    .map(transformApplicantToProfile);
  const guarantorCards = displayApplicants
    .filter((a) => a.role === "guarantor")
    .map(transformApplicantToProfile);

  return (
    <div className="linked-report">
      <div className="linked-report__container" id="divToPrint">
        {/* Header with date and back button — back button hidden in skor-sdk */}
        <div className="report-overview__header">
          {!isSkorSdk ? (
            <Button
              variant="ghost"
              size="sm"
              className="no-print"
              iconStart={<Icon name="chevronLeft" />}
              onClick={() =>
                navigate(workspaceId ? `/${workspaceId}/home` : "/home")
              }
            >
              {_t("goBack")}
            </Button>
          ) : (
            <div className="linked-report__powered no-print">
              <PoweredBySkor />
            </div>
          )}
          <PageHeader
            title={[
              `${addressVal?.street ?? ""} ${addressVal?.street_number ?? ""}`.trim(),
              `${addressVal?.floor_number ? addressVal?.floor_number : ""} ${addressVal?.flat_number ? `-${addressVal.flat_number}` : ""}`.trim(),
              addressVal?.province,
            ]
              .filter(Boolean)
              .join(", ")}
            actions={
              isSkorSdk ? (
                <Button
                  className="no-print"
                  onClick={() => setShowRequestUpdateModal(true)}
                >
                  {_t("requestUpdate", "Request update")}
                </Button>
              ) : processingTenantNames.length ? null : (
                <Button
                  variant="accent"
                  className="no-print"
                  loading={showSpinner}
                  iconStart={<Icon name="download" />}
                  onClick={downloadReportPdf}
                >
                  {showSpinner
                    ? `${_t("downloading")}...`
                    : _t("downloadReport")}
                </Button>
              )
            }
          />

          <div className="report-overview__subinfo">
            <span>
              {[addressVal?.postcode, addressVal?.muncipality]
                .filter(Boolean)
                .join(", ")}
            </span>
            <span className="report-overview__dot">•</span>
            <span>€{effectiveRentState || rent}/month</span>
          </div>

          <div className="report-overview__meta">
            <div className="report-overview__meta-item">
              <img src="/images/calendar-icon.svg" alt="" />
              <span>
                {todaysDate(
                  awardSnapshotCompleted || historyEventId
                    ? addressVal?.award_snapshot_info?.created_at
                    : null
                )}
              </span>
            </div>
            <div className="report-overview__meta-item">
              <img src="/images/multiple-user.svg" alt="" />
              <span>
                {effectiveSharedProfileIdsState?.length ??
                  sharedProfileIds?.length}{" "}
                {_t("tenantsSelected")}
              </span>
            </div>
          </div>
        </div>

        {processingTenantNames.length > 0 ? (
          <section className="linked-report__processing-state linked-report__processing-state--page">
            {renderProcessingPlaceholder()}
            <Notice
              layout="stack"
              className="linked-report__processing-message"
              title={_t(
                "reportWaitingForProfiles",
                "Waiting for tenant profiles"
              )}
            >
              <p>
                {_t(
                  "waitUntilProfileProcessingCompletes",
                  "Please wait until processing is complete."
                )}
              </p>
              <ul className="linked-report__processing-names">
                {processingTenantNames.map((name, index) => (
                  <li key={index}>{name}</li>
                ))}
              </ul>
            </Notice>
          </section>
        ) : (
          <>
            {outdatedApplicants.length > 0 && (
              <div className="linked-report__outdated-banner">
                <div className="linked-report__outdated-banner-icon">
                  <img src="/images/warning-dark-yellow.svg" alt="" />
                </div>
                <div className="linked-report__outdated-banner-content">
                  <strong>
                    {outdatedApplicants.length === applicants.length
                      ? `${outdatedApplicants.length} profiles are outdated`
                      : `${outdatedApplicants.length} of ${applicants.length} profiles are outdated`}
                  </strong>{" "}
                  {_t("outdatedProfilesInReportBody")}
                  <div className="linked-report__outdated-request-status no-print">
                    <span className="linked-report__outdated-request-chip">
                      {reminderSentAt && (
                        <svg
                          className="linked-report__request-clock-icon"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" />
                        </svg>
                      )}
                      {_t(
                        reminderSentAt ? "profileUpdateRequested" : "outdated",
                        reminderSentAt ? "Update requested" : "Outdated"
                      )}
                    </span>
                    {reminderSentAt && (
                      <span>
                        {_t("reminderSent", "Sent")}{" "}
                        {moment(reminderSentAt).fromNow()} ·{" "}
                        {_t(
                          "reportRefreshesAfterUpdate",
                          "the report refreshes once they update."
                        )}
                      </span>
                    )}
                  </div>
                  {reminderSentAt &&
                    Date.now() - new Date(reminderSentAt).getTime() <
                      24 * 60 * 60 * 1000 && (
                      <span className="linked-report__outdated-cooldown no-print">
                        {_t(
                          "profileUpdateReminderCooldown",
                          "You can resend in {{N}}h",
                          undefined,
                          {
                            N: Math.max(
                              1,
                              Math.ceil(
                                (24 * 60 * 60 * 1000 -
                                  (Date.now() -
                                    new Date(reminderSentAt).getTime())) /
                                  (60 * 60 * 1000)
                              )
                            ),
                          }
                        )}
                      </span>
                    )}
                  <p className="linked-report__pdf-outdated-caveat pdf-only">
                    {_t(
                      "pdfOutdatedProfilesCaveat_one",
                      "This report includes outdated profiles ({{count}} of {{total}}). Figures reflect each profile's last update date, shown per person, and may not reflect their current situation. Report generated {{date}}.",
                      undefined,
                      {
                        count: outdatedApplicants.length,
                        total: applicants.length,
                        date: todaysDate(),
                      }
                    )}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  className="linked-report__outdated-banner-cta no-print"
                  iconStart={<Icon name="refresh" />}
                  onClick={handleOpenOutdatedModal}
                >
                  {_t("resendProfileUpdate", "Resend")} (
                  {outdatedApplicants.length})
                </Button>
              </div>
            )}

            <DecisionCard
              decisionTitle={_t(decisionTitle)}
              decisionStatus={decisionTitle}
              combinedIncome={combinedIncome}
              combinedEffortRate={combinedEffortRate}
              decisionSummary={apiReport?.decisionSummary}
              insurabilityStatus={cachedInsurableStatus}
              insurabilityErrorCode={cachedInsuranceErrorCode}
              insurabilityErrorCodes={cachedInsuranceErrorCodes}
              currentLang={currentLang}
            >
              <VerdictPanel
                _t={_t}
                individualWarnings={individualWarnings}
                individualResolutions={individualResolutions}
                selectedResolveTenantIds={selectedResolveTenantIds}
                toggleResolveTenant={toggleResolveTenant}
                formatMissingDoc={formatMissingDoc}
                expandedDocs={expandedDocs}
                forceExpandDocs={isPdfExporting}
                toggleDocsExpanded={toggleDocsExpanded}
                hasPendingChanges={hasPendingChanges}
                resolvingDocs={resolvingDocs}
                resolveActionLabel={resolveActionLabel}
                onResolveClick={() => setShowResolveNoteModal(true)}
                blockingRows={blockingRows}
                kycWarnings={kycWarnings}
                onKycTenantClick={handleKycTenantClick}
                isVerdictLoading={isVerdictLoading}
              />
            </DecisionCard>

            <h2 className="linked-report__section-title">{_t("tenants")}</h2>
            {tenantCards.map(renderCard)}

            <h2 className="linked-report__section-title">{_t("guarantors")}</h2>
            {guarantorCards.length > 0 ? (
              guarantorCards.map(renderCard)
            ) : (
              <article className="linked-report__card">
                <div className="linked-report__documentation">
                  <div className="linked-report__row">
                    <span>{_t("noGuarantorsFound")}</span>
                    <span className="linked-report__muted">
                      {currentLang === "es" ? "Sin datos" : "No data"}
                    </span>
                  </div>
                </div>
              </article>
            )}
          </>
        )}
      </div>

      <ProfileUpdateRequestModal
        isOpen={showOutdatedModal}
        tenants={outdatedTenants}
        requestedAt={reminderSentAt}
        currentLang={currentLang}
        isSending={isSendingReminder}
        onClose={handleCloseOutdatedModal}
        onSend={handleSendReminder}
      />

      <RequestTenantUpdateModal
        isOpen={showRequestUpdateModal}
        tenants={requestUpdateTenants}
        isSending={isSendingFeedback}
        onClose={() => setShowRequestUpdateModal(false)}
        onSend={handleSendRequestUpdate}
      />

      <Modal
        open={showResolveNoteModal}
        onClose={() => setShowResolveNoteModal(false)}
        title={resolveActionLabel}
        align="start"
        actionsLayout="end"
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setShowResolveNoteModal(false)}
            >
              {_t("cancel")}
            </Button>
            <Button
              size="lg"
              loading={resolvingDocs}
              onClick={() => handleApplyResolutions(resolveNote)}
            >
              {resolvingDocs
                ? `${_t("updating", "Updating")}...`
                : resolveActionLabel}
            </Button>
          </>
        }
      >
        <p className="resolve-documents-modal__summary">
          {pendingResolveIds.length > 0 && pendingUnresolveIds.length > 0
            ? _t("updatingDocumentsFor", "Updating documents for")
            : pendingUnresolveIds.length > 0
              ? _t("unresolvingDocumentsFor", "Unresolving documents for")
              : _t("resolvingDocumentsFor", "Resolving documents for")}{" "}
          <strong>{selectedResolveTenantIds.length}</strong>{" "}
          {_t("tenantS", "tenant(s)")}
        </p>
        {pendingResolveIds.length > 0 && (
          <div className="resolve-documents-modal__field">
            <TextArea
              id="report-resolve-note"
              placeholder={_t("addNote", "Add a note (optional)")}
              aria-label={_t("addNote", "Add a note (optional)")}
              maxLength={500}
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
            />
            <span className="resolve-documents-modal__count">
              {resolveNote?.length}/500
            </span>
          </div>
        )}
        {pendingUnresolveIds.length > 0 && pendingResolveIds.length === 0 && (
          <p className="resolve-documents-modal__hint">
            {_t(
              "unresolveHint",
              "This will mark the documents as unresolved again."
            )}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default LinkedReportOverview;
